const escapeHTML = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (token) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[token])
  );

const formatMoney = (value, currencyCode = "EUR") =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currencyCode || "EUR",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
};

const buildBaseHTML = ({ title, subtitle, metaRows, tableHeaders, tableRows, totals }) => `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHTML(title)}</title>
  <style>
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; color: #111827; font-family: Arial, Helvetica, sans-serif; }
    body { padding: 24px; }
    @page { size: A4 portrait; margin: 14mm; }
    .sheet { max-width: 820px; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; margin-bottom: 20px; }
    .brand { font-size: 24px; font-weight: 800; letter-spacing: 0.04em; }
    .doc-title { font-size: 28px; font-weight: 700; margin: 0 0 4px; }
    .subtitle { color: #6b7280; font-size: 14px; margin: 0; }
    .meta-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 24px; margin: 24px 0; }
    .meta-label { color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; }
    .meta-value { font-size: 16px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    thead th { text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; color: #374151; border-bottom: 1px solid #d1d5db; padding: 10px 8px; }
    tbody td { border-bottom: 1px solid #e5e7eb; padding: 12px 8px; vertical-align: top; font-size: 14px; }
    .item-title { font-weight: 700; margin-bottom: 2px; }
    .item-subtitle, .item-note { color: #6b7280; font-size: 12px; }
    .money, .qty { white-space: nowrap; }
    .totals { margin-top: 18px; margin-left: auto; width: min(320px, 100%); }
    .totals-row { display: flex; justify-content: space-between; gap: 16px; padding: 6px 0; font-size: 14px; }
    .totals-row.total { font-size: 18px; font-weight: 800; border-top: 1px solid #111827; margin-top: 4px; padding-top: 12px; }
    .footer-note { margin-top: 28px; color: #6b7280; font-size: 12px; }
  </style>
</head>
<body>
  <main class="sheet">
    <header class="header">
      <div class="brand">Black&White</div>
      <div>
        <h1 class="doc-title">${escapeHTML(title)}</h1>
        <p class="subtitle">${escapeHTML(subtitle)}</p>
      </div>
    </header>
    <section class="meta-grid">
      ${metaRows
        .map(
          (row) => `
            <div>
              <div class="meta-label">${escapeHTML(row.label)}</div>
              <div class="meta-value">${escapeHTML(row.value)}</div>
            </div>
          `
        )
        .join("")}
    </section>
    <table>
      <thead>
        <tr>
          ${tableHeaders.map((header) => `<th>${escapeHTML(header)}</th>`).join("")}
        </tr>
      </thead>
      <tbody>
        ${tableRows.join("")}
      </tbody>
    </table>
    <section class="totals">
      ${totals
        .map(
          (row) => `
            <div class="totals-row ${row.emphasis ? "total" : ""}">
              <span>${escapeHTML(row.label)}</span>
              <span>${escapeHTML(row.value)}</span>
            </div>
          `
        )
        .join("")}
    </section>
    <div class="footer-note">Generated from immutable commerce record snapshots.</div>
  </main>
  <script>
    window.addEventListener("load", function () {
      setTimeout(function () { window.print(); }, 80);
    });
  </script>
</body>
</html>`;

const openPrintHTML = (html) => {
  const popup = window.open("", "_blank");

  if (popup && popup.document) {
    popup.document.open();
    popup.document.write(html);
    popup.document.close();
    return;
  }

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  const { contentDocument, contentWindow } = iframe;
  if (!contentDocument || !contentWindow) {
    document.body.removeChild(iframe);
    throw new Error("Print iframe not available");
  }

  contentDocument.open();
  contentDocument.write(html);
  contentDocument.close();

  contentWindow.addEventListener("afterprint", () => {
    setTimeout(() => {
      try {
        document.body.removeChild(iframe);
      } catch (_error) {}
    }, 400);
  });
};

export const printSaleReceipt = (sale) => {
  if (!sale?.id) {
    throw new Error("Sale detail is not available to print.");
  }

  const currencyCode = sale.currencyCode || "EUR";
  const tableRows = (sale.lines || []).map(
    (line) => `
      <tr>
        <td>
          <div class="item-title">${escapeHTML(line.productTitle || "")}</div>
          <div class="item-subtitle">${escapeHTML(line.variantTitle || "")}</div>
          ${line.optionSummary ? `<div class="item-note">${escapeHTML(line.optionSummary)}</div>` : ""}
        </td>
        <td class="qty">${escapeHTML(String(line.quantity || 0))}</td>
        <td class="money">${
          Number(line.discountTotalAmount || 0) > 0
            ? `${escapeHTML(formatMoney(line.finalUnitPriceAmount, currencyCode))}<div class="item-note">Was ${escapeHTML(formatMoney(line.unitPriceAmount, currencyCode))}</div>`
            : escapeHTML(formatMoney(line.finalUnitPriceAmount, currencyCode))
        }</td>
        <td class="money">${escapeHTML(formatMoney(line.lineSubtotalAmount, currencyCode))}</td>
      </tr>
    `
  );

  openPrintHTML(
    buildBaseHTML({
      title: "Sale Receipt",
      subtitle: sale.saleNumber || "Receipt",
      metaRows: [
        { label: "Sale number", value: sale.saleNumber || "—" },
        { label: "Completed", value: formatDateTime(sale.completedAt) || "—" },
        { label: "Location", value: sale.location?.name || "—" },
        { label: "Payment", value: sale.paymentMethod || "—" },
      ],
      tableHeaders: ["Item", "Qty", "Price", "Total"],
      tableRows,
      totals: [
        { label: "Subtotal", value: formatMoney(sale.subtotalAmount, currencyCode) },
        { label: "Discount", value: formatMoney(sale.discountTotalAmount, currencyCode) },
        { label: "Total", value: formatMoney(sale.totalAmount, currencyCode), emphasis: true },
      ],
    })
  );
};

/** Print a consolidated list of sales for the selected day (and filters). */
export const printDailySalesInvoice = ({
  date,
  locationLabel,
  saleNumberFilter,
  sales,
}) => {
  if (!Array.isArray(sales) || !sales.length) {
    throw new Error("No sales to print for this day.");
  }

  const currencyCode = sales[0]?.currencyCode || "EUR";
  const grandTotal = sales.reduce(
    (sum, s) => sum + Number(s.totalAmount ?? s.total ?? 0),
    0
  );

  const tableRows = sales.map(
    (sale) => `
      <tr>
        <td><div class="item-title">${escapeHTML(sale.saleNumber || "")}</div>
            <div class="item-subtitle">${escapeHTML(sale.itemSummary || "")}</div></td>
        <td class="qty">${escapeHTML(formatDateTime(sale.completedAt) || "—")}</td>
        <td>${escapeHTML(sale.location?.name || "—")}</td>
        <td>${escapeHTML(String(sale.paymentMethod || "—"))}</td>
        <td class="money">${escapeHTML(
          formatMoney(sale.totalAmount ?? sale.total, sale.currencyCode || currencyCode)
        )}</td>
      </tr>
    `
  );

  const scopeNote = saleNumberFilter
    ? `Sale number contains "${saleNumberFilter}".`
    : "All sales matching the current filters.";

  openPrintHTML(
    buildBaseHTML({
      title: "Daily sales invoice",
      subtitle: `${escapeHTML(date || "")}${locationLabel ? ` · ${escapeHTML(locationLabel)}` : ""}`,
      metaRows: [
        { label: "Date", value: date || "—" },
        { label: "Location", value: locationLabel || "All locations" },
        { label: "Sales in document", value: String(sales.length) },
        { label: "Printed at", value: formatDateTime(new Date().toISOString()) || "—" },
        { label: "Scope", value: scopeNote },
      ],
      tableHeaders: ["Sale", "Completed", "Location", "Payment", "Total"],
      tableRows,
      totals: [
        {
          label: "Grand total",
          value: formatMoney(grandTotal, currencyCode),
          emphasis: true,
        },
      ],
    })
  );
};

export const printReturnRefundSlip = (returnRecord) => {
  if (!returnRecord?.id) {
    throw new Error("Return detail is not available to print.");
  }

  const currencyCode = returnRecord.currencyCode || "EUR";
  const tableRows = (returnRecord.lines || []).map(
    (line) => `
      <tr>
        <td>
          <div class="item-title">${escapeHTML(line.productTitle || "")}</div>
          <div class="item-subtitle">${escapeHTML(line.variantTitle || "")}</div>
          ${line.optionSummary ? `<div class="item-note">${escapeHTML(line.optionSummary)}</div>` : ""}
        </td>
        <td class="qty">${escapeHTML(String(line.returnedQuantity || 0))}</td>
        <td class="money">${escapeHTML(formatMoney(line.refundAmount, currencyCode))}</td>
      </tr>
    `
  );

  openPrintHTML(
    buildBaseHTML({
      title: "Refund Slip",
      subtitle: returnRecord.returnNumber || "Refund",
      metaRows: [
        { label: "Return number", value: returnRecord.returnNumber || "—" },
        { label: "Linked sale", value: returnRecord.originalSale?.saleNumber || "—" },
        { label: "Completed", value: formatDateTime(returnRecord.completedAt) || "—" },
        { label: "Location", value: returnRecord.location?.name || "—" },
      ],
      tableHeaders: ["Item", "Qty", "Refund"],
      tableRows,
      totals: [
        {
          label: "Total refunded",
          value: formatMoney(returnRecord.totalRefundAmount, currencyCode),
          emphasis: true,
        },
      ],
    })
  );
};
